import classNames from "classnames"

import type { ButtonProps } from "./Button"

export const BUTTON_BASE_CLASSES =
  "inline-flex w-full items-center touch-none select-none text-center gap-x-2 relative shrink-0 justify-center focus:outline-none focus:!ring-2 focus:!ring-indigo-500 focus:ring-offset-2 active:translate-y-px disabled:pointer-events-none disabled:opacity-50"

export const BUTTON_IMPACT_CLASSES: Record<
  NonNullable<ButtonProps["variant"]>,
  Record<NonNullable<ButtonProps["impact"]>, string>
> = {
  primary: {
    bold: "text-white bg-blue-500 hover:bg-blue-700",
    light: "text-blue-500 bg-white hover:bg-gray-50",
    none: "text-blue-500 bg-transparent hover:bg-gray-50",
  },
  secondary: {
    bold: "border border-gray-300 text-white bg-gray-500 hover:bg-gray-700",
    light: "border border-gray-300 text-gray-900 bg-white hover:bg-gray-50",
    none: "text-gray-900 bg-transparent hover:bg-gray-50",
  },
  text: {
    bold: "text-white bg-gray-500 hover:bg-gray-700",
    light: "text-blue-500 bg-white hover:bg-gray-50",
    none: "text-blue-500 bg-transparent hover:bg-gray-100",
  },
  error: {
    bold: "text-white bg-red-500 hover:bg-red-700 focus:ring-red-500",
    light: "border border-red-500 text-red-500 bg-white hover:bg-gray-50 focus:ring-red-500",
    none: "text-red-500 hover:text-red-700 bg-transparent hover:bg-gray-50 focus:ring-red-500",
  },
  unstyled: {
    bold: "",
    light: "",
    none: "",
  },
}

export const BUTTON_SIZE_CLASSES: Record<NonNullable<ButtonProps["size"]>, string> = {
  xs: "px-2 py-2 w-7 h-7 text-button3/semibold",
  sm: "px-2.5 py-2.5 w-9 h-9 text-button2/semibold",
  "sm-light": "px-2.5 py-2.5 w-9 h-9 text-button2/regular",
  md: "px-3 py-3 h-[38px] w-[38px] text-button2/semibold",
  "md-light": "px-3 py-3 h-[38px] w-[38px] text-button2/regular",
  lg: "px-3.5 py-3.5 h-12 w-12 text-button1/semibold",
  xl: "px-4 py-4  h-[52px] w-[52px] text-button1/semibold",
}

export const BUTTON_SHAPE_CLASSES: Record<NonNullable<ButtonProps["shape"]>, string> = {
  square: "rounded-none",
  rounded: "rounded-md",
  pill: "rounded-full",
}

export const BUTTON_MIN_WIDTH_CLASSES: Record<NonNullable<ButtonProps["minWidth"]>, string> = {
  none: "",
  xl: "min-w-[100px]",
}

/**
 * Helper function to generate button classes based on configuration
 */
export const getButtonClasses = ({
  variant = "primary",
  impact = "bold",
  size = "md",
  shape = "rounded",
  className = "",
}: Partial<ButtonProps> & { className?: string }) => {
  return classNames(
    BUTTON_BASE_CLASSES,
    BUTTON_IMPACT_CLASSES[variant][impact],
    BUTTON_SIZE_CLASSES[size],
    BUTTON_SHAPE_CLASSES[shape],
    className
  )
}
